import type { ReactNode } from "react";
import { cn } from "../cn";

const PATHS = {
  lamp: (
    <>
      <path d="M3.5 12.5H16l4.5-3" />
      <path d="M4.5 12.5c0 3 3 5.2 7 5.2s7-2.2 7-5.2" />
      <path d="M8.5 12.5a3 3 0 0 1 6 0" />
      <path d="M8 20.5h8M10.2 17.7l-.7 2.8M13.8 17.7l.7 2.8" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  send: <path d="M12 19V5M5.5 11.5 12 5l6.5 6.5" />,
  stop: <rect x="6" y="6" width="12" height="12" rx="2" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  back: <path d="M19 12H5M11 6l-6 6 6 6" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.2-4.2" />
    </>
  ),
  chevron: <path d="m9 6 6 6-6 6" />,
  caret: <path d="m6 9 6 6 6-6" />,
  more: (
    <>
      <circle cx="5.5" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1" fill="currentColor" />
    </>
  ),
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5v-2a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2" />
    </>
  ),
  download: <path d="M12 4v11M7 10.5l5 5 5-5M5 20h14" />,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  refresh: (
    <path d="M4 12a8 8 0 0 1 13.7-5.6L20 9M20 4v5h-5M20 12a8 8 0 0 1-13.7 5.6L4 15M4 20v-5h5" />
  ),
  trash: (
    <>
      <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="m6 7 1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12M10 11v5M14 11v5" />
    </>
  ),
  coins: (
    <>
      <path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17" />
      <path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
      <path d="m2 16 6 6" />
      <circle cx="16" cy="9" r="2.9" />
      <circle cx="6" cy="5" r="3" />
    </>
  ),
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  gamepad: (
    <>
      <path d="M7 8h10a4 4 0 0 1 3.9 4.9l-.6 2.8a2.3 2.3 0 0 1-4 1L14.5 15h-5l-1.8 1.7a2.3 2.3 0 0 1-4-1l-.6-2.8A4 4 0 0 1 7 8Z" />
      <path d="M8 10.5v3M6.5 12h3" />
      <path d="M15.5 11.5h.01M17.5 13h.01" />
    </>
  ),
  home: (
    <>
      <path d="M3.5 10.5 12 3.5l8.5 7" />
      <path d="M5.5 9v11h13V9" />
      <path d="M10 20v-5.5h4V20" />
    </>
  ),
  external: (
    <path d="M14 5h5v5M19 5l-8 8M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4" />
  ),
} satisfies Record<string, ReactNode>;

/** Every icon the console draws, by name. One module, one grid. */
export type IconName = keyof typeof PATHS;
export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export interface IconProps {
  name: IconName;
  /** `sm` is 16px, the working size; `md` is 20px, for a mark standing alone. */
  size?: "sm" | "md";
  className?: string;
}

/**
 * A 24-unit grid, stroked at 1.8 with round caps and joins, in `currentColor`,
 * drawn at `--icon-sm` or `--icon-md`. It is decoration: whatever carries it
 * carries the name.
 */
export function Icon({ name, size = "sm", className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-mark={name === "lamp" ? "lamp" : undefined}
      className={cn(
        "shrink-0",
        size === "sm" ? "size-[var(--icon-sm)]" : "size-[var(--icon-md)]",
        className,
      )}
    >
      {PATHS[name]}
    </svg>
  );
}
